import { HUB_BASE_URL } from "@constants/env";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { getAuthToken } from "@helpers/auth-provider";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { isWeb } from "@memorang/helpers/platform";
import { safeJsonParse } from "@memorang/helpers/safe-json";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	SubskillAssessment,
} from "@memorang/types";
import { asPassage } from "@memorang/types";
import type { Passage } from "@memorang/types";
import { useToast } from "@memorang/ui/components/Toast";
import { authHeaders } from "@memorang/ui/contexts/AuthTokenContext";
import { runOnJS } from "react-native-reanimated";
import EventSource from "react-native-sse";
import {
	getAssessmentCategory,
	getAssessorSubtaskType,
} from "../helpers/ai-assessment";
import { uploadRecording } from "../helpers/recording";
import type {
	SSEDataPayload,
	SSEMessage,
	SSERunDetails,
} from "../types/ai-feedback";
import useAIFeedbackStore from "./useAIFeedbackStore";
import useCurrentSession from "./useCurrentSession";
import useSessionStore from "./useSessionStore";

type GenerateExplanationParams = {
	currentItem: BaseItemSchema;
	itemType: OriginalItemTypes;
	onAiFeedbackProvided: () => void;
	audioUrl?: string | null;
	existingUserResponse?: string;
};

const MAX_RETRIES = 3;

const formatLanguageCode = (languageCode: string) => {
	return languageCode.replace(/-/g, "_").toLowerCase();
};

export const replaceBrackets = (str: string): string =>
	str.replace(/[{}[\]]/g, "");

const getStemAndTranscript = (item: BaseItemSchema) => {
	const stimulusContent = item.stimulus;

	let readingPassage: Passage | null = null;

	let listeningTranscript: string[] = [];

	if (stimulusContent) {
		const passageObj = asPassage(stimulusContent.passage);
		const passageStr =
			typeof stimulusContent.passage === "string"
				? stimulusContent.passage
				: null;
		const stringAsPassage: Passage | null = passageStr
			? { title: "", paragraphs: [{ number: 1, text: passageStr }] }
			: null;
		readingPassage =
			stimulusContent.slides?.find((slide) => slide.title === "Read")
				?.passage ||
			passageObj ||
			stringAsPassage ||
			null;
	}

	if (item.content?.stimulus?.text) {
		readingPassage = {
			title: item.content?.stimulus?.title || "",
			paragraphs: [
				{
					number: 1,
					text: item.content?.stimulus?.text,
				},
			],
		};

		if (stimulusContent) {
			const slideTranscripts = stimulusContent.slides
				?.find((slide) => slide.title === "Listen")
				?.media?.map((media) => media.transcript);

			const mediaTranscripts = stimulusContent.media?.map(
				(media) => media.transcript || media.transcription,
			);

			listeningTranscript =
				slideTranscripts?.filter((t): t is string => !!t) ||
				mediaTranscripts?.filter((t): t is string => !!t) ||
				[];
		}
		if (item.content?.stimulus?.media) {
			listeningTranscript = item.content.stimulus.media
				.map((media) => media.transcript || media.transcription)
				.filter((t): t is string => !!t);
		}
		if (item.content?.media) {
			listeningTranscript = item.content.media
				.map((media) => media.transcript || media.transcription)
				.filter((t): t is string => !!t);
		}
	}

	return {
		readingPassage,
		listeningTranscript,
	};
};
/**
 * Formats the item for the AI feedback endpoint based on the item type
 * @param item
 */
const formatItemForAI = (item: BaseItemSchema) => {
	const { stimulus, content, metadata, stimulusMetadata } = item;

	const stimulusPassage = asPassage(stimulus?.passage);
	const stimulusInstruction = stimulus?.instruction;
	const contentInstruction = item.content?.instruction;
	const baseFormat = {
		instruction:
			(typeof stimulusInstruction === "string"
				? stimulusInstruction
				: stimulusInstruction?.text) ||
			(typeof contentInstruction === "string"
				? contentInstruction
				: contentInstruction?.text),
		stem: content?.stem?.text,
	};

	if (metadata?.type && !stimulusMetadata?.type) {
		switch (metadata?.type) {
			case "SPEAKING_RESPONSE":
			case "SPEAKING_READ_ALOUD":
			case "SPEAKING_EXTENDED_RESPONSE":
			case "LONG_TURN":
			case "SPEAKING_SHORT_RESPONSE":
			case "STORY_TELLING": {
				const { listeningTranscript } = getStemAndTranscript(item);

				return {
					...baseFormat,
					transcripts: listeningTranscript?.join("\n"),
				};
			}

			case "WRITING_EXTENDED_RESPONSE":
			case "WRITING_INTEGRATED_RESPONSE":
			case "WRITING_RESPONSE":
			case "WRITING_EMAIL": {
				const { readingPassage, listeningTranscript } =
					getStemAndTranscript(item);

				return {
					...baseFormat,
					passageTitle: stimulusPassage?.title,
					readingPassageTitle: readingPassage?.title,
					readingPassageText: readingPassage?.paragraphs
						?.map((paragraph) => paragraph.text)
						.join("\n"),
					listeningTranscript: listeningTranscript?.join("\n\n"),
				};
			}

			default:
				return baseFormat;
		}
	}

	switch (stimulusMetadata?.type) {
		case "RLS_STIMULUS": {
			const { readingPassage, listeningTranscript } =
				getStemAndTranscript(item);

			return {
				...baseFormat,
				readingPassageTitle: readingPassage?.title,
				readingPassageText: readingPassage?.paragraphs
					?.map((paragraph) => paragraph.text)
					.join("\n"),
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "LS_STIMULUS": {
			const { listeningTranscript } = getStemAndTranscript(item);

			return {
				...baseFormat,
				passageTitle: stimulusPassage?.title,
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "ACADEMIC_DISCUSSION_STIMULUS": {
			return {
				...baseFormat,
				className: stimulus?.className,
				professorName: stimulus?.professor?.name,
				questionTitle: stimulus?.professor?.questionTitle,
				questionBody: stimulus?.professor?.questionBody,
				discussion: content?.discussion
					?.map((d) => `${d.student.name}: ${d.post}`)
					.join("\n\n"),
			};
		}

		case "INTEGRATED_WRITING_STIMULUS": {
			const { readingPassage, listeningTranscript } =
				getStemAndTranscript(item);

			return {
				...baseFormat,
				passageTitle: stimulusPassage?.title,
				readingPassageTitle: readingPassage?.title,
				readingPassageText: readingPassage?.paragraphs
					?.map((paragraph) => paragraph.text)
					.join("\n"),
				listeningTranscript: listeningTranscript?.join("\n\n"),
			};
		}

		case "INDEPENDENT_SPEAKING_STIMULUS": {
			const { listeningTranscript } = getStemAndTranscript(item);

			return {
				...baseFormat,
				transcripts: listeningTranscript?.join("\n"),
			};
		}
		default:
			return baseFormat;
	}
};

export const getParentStem = (currentItem: BaseItemSchema) => {
	const { stimulus, content, metadata } = currentItem;

	let parentStem = "";

	if (stimulus?.stem?.text) {
		parentStem = JSON.stringify(stimulus?.stem?.text);
	}

	const stimPassage = asPassage(stimulus?.passage);
	if (stimPassage?.paragraphs?.length) {
		parentStem = JSON.stringify(
			stimPassage.paragraphs.map((paragraph) => paragraph.text).join("\n"),
		);
	} else if (typeof stimulus?.passage === "string" && stimulus.passage) {
		parentStem = JSON.stringify(stimulus.passage);
	}

	if (content?.stimulus?.text) {
		parentStem = JSON.stringify(content?.stimulus?.text);
	}

	if (content?.discussion) {
		parentStem += `\n\nDiscussion: ${JSON.stringify(content?.discussion)}`;
	}

	if (content?.instruction) {
		const instructionText =
			typeof content.instruction === "string"
				? content.instruction
				: content.instruction?.text;
		parentStem += `\n\nInstruction: ${instructionText}`;
	}

	if (metadata?.skill) {
		parentStem += `\n\nCurrent Skill: ${metadata?.skill}`;
	}

	return replaceBrackets(parentStem);
};

export const useAIFeedback = () => {
	const sessionId = useSessionStore((state) => state.id);
	const setStreaming = useAIFeedbackStore((store) => store.setStreaming);
	const setShowError = useAIFeedbackStore((store) => store.setShowError);
	const updateTranscription = useAIFeedbackStore(
		(store) => store.updateTranscription,
	);
	const userResponse = useAIFeedbackStore((store) => store.userResponse);
	const audioResponse = useAIFeedbackStore((store) => store.audioResponse);
	const updateRunDetails = useAIFeedbackStore(
		(store) => store.updateRunDetails,
	);
	const updateSubskillAssessments = useAIFeedbackStore(
		(store) => store.updateSubskillAssessments,
	);
	const updateScore = useAIFeedbackStore((store) => store.updateScore);
	const updateExplanation = useAIFeedbackStore(
		(store) => store.updateExplanation,
	);
	const updateAnswer = useAIFeedbackStore((store) => store.updateAnswer);
	const tenantId = useAppStore((store) => store.tenant.id);
	const { showToast } = useToast();
	const { userLogicalId } = useAuthContext();
	const language = formatLanguageCode(i18n.language);
	const { currentItem } = useCurrentSession();

	const handleGenerateExplanation = async ({
		currentItem,
		itemType,
		audioUrl,
		existingUserResponse,
		onAiFeedbackProvided,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	}: GenerateExplanationParams) => {
		const isRecording =
			getAssessmentCategory(currentItem.response.type) === "speaking";
		const assessmentType = getAssessorSubtaskType(currentItem.response.type);

		let stimulusTaskType: string =
			currentItem.stimulusMetadata?.type ?? itemType ?? "";

		if (
			stimulusTaskType &&
			["LS_STIMULUS", "RLS_STIMULUS"].includes(stimulusTaskType)
		) {
			stimulusTaskType = "INTEGRATED_SPEAKING_STIMULUS";
		}

		const formattedItem = formatItemForAI(currentItem);
		const answer = isRecording ? audioUrl : existingUserResponse;
		if (answer) {
			updateAnswer(currentItem.id!, answer);
		}

		const getMediaUrls = (): string[] => {
			const urls: string[] = [];

			if (currentItem.stimulus?.media) {
				urls.push(...currentItem.stimulus.media.map((media) => media.url));
			}

			if (currentItem.content?.media) {
				urls.push(...currentItem.content.media.map((media) => media.url));
			}

			if (currentItem.content?.stimulus?.media) {
				urls.push(
					...currentItem.content.stimulus.media.map((media) => media.url),
				);
			}

			return urls;
		};

		const body = {
			tenantId,
			userId: userLogicalId,
			itemId: currentItem.id,
			...(sessionId ? { sessionId } : {}),
			options: {
				subtaskType: assessmentType,
				input: {
					...formattedItem,
					stimulusTaskType: stimulusTaskType,
					assessment_mode:
						itemType.includes("SPEAKING") || itemType.includes("STORY")
							? "Summative"
							: "Formative",
					imageUrls: {
						media: getMediaUrls(),
					},
					...(assessmentType === "langgraph-speaking" && audioUrl
						? { audioUrls: [audioUrl] }
						: {}),
					...(assessmentType === "langgraph-writing" &&
						existingUserResponse && {
							writing_response: String(existingUserResponse).trim(),
						}),
					...(assessmentType === "langgraph-completion" &&
						existingUserResponse && {
							completion_response: JSON.stringify(existingUserResponse),
						}),
					feedbackInputs: {
						language,
					},
				},
			},
		};

		setStreaming(true);
		setShowError(false);

		const token = await getAuthToken();
		const es = new EventSource(`${HUB_BASE_URL}${HUB_ENDPOINTS.ASSESSOR}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "text/event-stream",
				...authHeaders(token),
			},
			body: JSON.stringify(body),
		});

		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		es.addEventListener("message", (event) => {
			if (!event.data) {
				return;
			}
			const message = safeJsonParse<SSEMessage>(event.data);
			if (!message) {
				return;
			}

			if (message.event === "close") {
				es.close();
			}

			if (message.event === "runDetails") {
				updateRunDetails(
					currentItem.id!,
					message.data as SSERunDetails | undefined,
				);
			}
			if (message.event === "toolCall") {
				const data = message.data as SSEDataPayload | undefined;
				const tool = data?.tool;
				const response = data?.response;

				if (tool === "transcriptionTool" && Array.isArray(response)) {
					const transcriptText = response
						.filter(
							(r): r is { url: string; text: string } =>
								typeof r?.text === "string",
						)
						.map((r) => r.text)
						.join("\n");

					updateTranscription(currentItem.id!, transcriptText);
				}
			}

			if (message.event === "data") {
				const data = message.data as SSEDataPayload | undefined;
				const node = data?.node;
				const response = data?.response;
				const res =
					response != null &&
					typeof response === "object" &&
					!Array.isArray(response)
						? (response as Record<string, unknown>)
						: undefined;

				if (node === "subskillAssessmentNode") {
					if (res?.subskillAssessments) {
						updateSubskillAssessments(
							currentItem.id!,
							res.subskillAssessments as SubskillAssessment[],
						);
					}
				}
				if (node === "overallAssessmentNode") {
					if (res?.score !== undefined) {
						updateScore(currentItem.id!, res.score as number);
					}
				}
				if (node === "feedbackNode") {
					if (res?.feedback) {
						runOnJS(updateExplanation)(currentItem.id!, res.feedback as string);
					}
				}
			}
		});

		es.addEventListener("close", () => {
			onAiFeedbackProvided();
			setStreaming(false);
		});

		es.addEventListener("error", (event) => {
			console.error("Error from SSE.", event);

			const retries =
				useAIFeedbackStore.getState().aiResponse?.[currentItem.id!]?.retries ??
				0;

			if (retries < MAX_RETRIES) {
				const incrementRetries = useAIFeedbackStore.getState().incrementRetries;
				incrementRetries(currentItem.id!);
				return;
			}
			showToast({
				message: "Something went wrong, please restart the session",
			});
			es.close();
		});
	};

	const generateExplanation = async (onAiFeedbackProvided: () => void) => {
		const itemType = currentItem.metadata?.type!;
		const existingUserResponse = userResponse?.[currentItem.id!];
		const existingAudioResponse = audioResponse?.[currentItem.id!];

		if (existingUserResponse || existingAudioResponse) {
			setStreaming(true);
			let audioUrl: string | null = null;

			const fileName = `${userLogicalId}-${sessionId}-${currentItem.id}.${isWeb ? "webm" : "m4a"}`;

			if (getAssessmentCategory(currentItem.response.type) === "speaking") {
				audioUrl = await uploadRecording(
					existingUserResponse as Blob | string,
					fileName,
				);
			}

			handleGenerateExplanation({
				currentItem,
				itemType,
				onAiFeedbackProvided,
				audioUrl,
				existingUserResponse: existingUserResponse as string,
			});
		}
	};

	return {
		generateExplanation,
	};
};
